/**
 * ProjectPipelineTimeline - Visual timeline of Deviz → Contract → Project flow
 * Shows the status of each work item across the pipeline stages
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, FileSpreadsheet, FileSignature, FolderOpen, ChevronRight, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGE_CONFIG = [
  { key: "deviz", label: "Deviz Ofertă", icon: FileSpreadsheet, color: "emerald" },
  { key: "contract", label: "Contract", icon: FileSignature, color: "indigo" },
  { key: "project", label: "Proiect", icon: FolderOpen, color: "amber" },
];

function StageIndicator({ active, completed, config, isLast }) {
  const Icon = config.icon;
  const colorMap = {
    emerald: { active: "bg-emerald-500 text-white", done: "bg-emerald-100 text-emerald-700 border-emerald-300", idle: "bg-slate-100 text-slate-400 border-slate-200" },
    indigo: { active: "bg-indigo-500 text-white", done: "bg-indigo-100 text-indigo-700 border-indigo-300", idle: "bg-slate-100 text-slate-400 border-slate-200" },
    amber: { active: "bg-amber-500 text-white", done: "bg-amber-100 text-amber-700 border-amber-300", idle: "bg-slate-100 text-slate-400 border-slate-200" },
  };
  const colors = colorMap[config.color];
  const style = completed ? colors.done : active ? colors.active : colors.idle;

  return (
    <div className="flex items-center gap-1.5">
      <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border text-xs font-medium transition-all ${style}`}>
        <Icon className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">{config.label}</span>
      </div>
      {!isLast && (
        <ArrowRight className={`w-3.5 h-3.5 flex-shrink-0 ${completed ? 'text-emerald-400' : 'text-slate-300'}`} />
      )}
    </div>
  );
}

function PipelineRow({ item }) {
  const currentStage = item.project_id ? 2 : item.contract_id ? 1 : 0;

  return (
    <div 
      className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 rounded-lg bg-slate-50/80 hover:bg-slate-100 transition-colors border border-slate-100"
      data-testid={`pipeline-row-${item.deviz_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-semibold text-sm text-slate-800 truncate">
            {item.beneficiar || "Client necunoscut"}
          </span>
          <Badge variant="outline" className="text-xs flex-shrink-0">
            {item.deviz_number || "Fără nr."}
          </Badge>
        </div>
        <p className="text-xs text-slate-500 truncate">
          {item.obiectiv || "Fără obiectiv"} 
          {item.total ? ` • ${Number(item.total).toLocaleString('ro-RO', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} RON` : ""}
        </p>
      </div>
      <div className="flex items-center gap-1">
        {STAGE_CONFIG.map((stage, idx) => (
          <StageIndicator
            key={stage.key}
            config={stage}
            completed={idx < currentStage}
            active={idx === currentStage}
            isLast={idx === STAGE_CONFIG.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

export default function ProjectPipelineTimeline({ companyId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) return;
    
    const fetchPipeline = async () => {
      try {
        const res = await axios.get(`${API}/pipeline/status?company_id=${companyId}`);
        setItems(res.data);
      } catch (err) {
        console.error("Pipeline fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchPipeline();
  }, [companyId]);

  const stats = {
    devizOnly: items.filter(i => !i.contract_id && !i.project_id).length,
    hasContract: items.filter(i => i.contract_id && !i.project_id).length,
    hasProject: items.filter(i => i.project_id).length,
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  if (!items.length) return null;

  return (
    <Card data-testid="pipeline-timeline">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <ChevronRight className="w-4 h-4 text-indigo-500" />
            Pipeline Lucrări
          </CardTitle>
          <div className="flex gap-2 text-xs">
            <Badge variant="outline" className="border-emerald-200 text-emerald-700">{stats.devizOnly} Oferte</Badge>
            <Badge variant="outline" className="border-indigo-200 text-indigo-700">{stats.hasContract} Contracte</Badge>
            <Badge variant="outline" className="border-amber-200 text-amber-700">{stats.hasProject} Proiecte</Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.slice(0, 10).map((item) => (
          <PipelineRow key={item.deviz_id} item={item} />
        ))}
        {items.length > 10 && (
          <p className="text-xs text-center text-slate-400 pt-2">
            + {items.length - 10} lucrări adiționale
          </p>
        )}
      </CardContent>
    </Card>
  );
}
